import React, { useEffect, useState, createContext, useContext } from 'react';
type Country = 'eg' | 'ae' | 'sa';
interface CountryContextType {
  country: Country;
  setCountry: (country: Country) => void;
  currency: string;
  currencySymbol: string;
  phoneNumber: string;
  whatsappNumber: string;
  pricing: {
    starter: {
      monthly: number;
      yearly: number;
    };
    business: {
      monthly: number;
      yearly: number;
    };
    pro: {
      monthly: number;
      yearly: number;
    };
    custom: {
      monthly: string;
      yearly: string;
    };
  };
}
const CountryContext = createContext<CountryContextType | undefined>(undefined);
const COUNTRY_CONFIG = {
  eg: {
    currency: 'EGP',
    currencySymbol: 'ج.م',
    phoneNumber: '+20 150 808 7501',
    whatsappNumber: '201508087501',
    pricing: {
      starter: {
        monthly: 1500,
        yearly: 14400
      },
      business: {
        monthly: 4000,
        yearly: 36000
      },
      pro: {
        monthly: 7500,
        yearly: 63000
      },
      custom: {
        monthly: 'تواصل معنا',
        yearly: 'Contact Us'
      }
    }
  },
  ae: {
    currency: 'AED',
    currencySymbol: 'درهم',
    phoneNumber: '+971 50 854 6970',
    whatsappNumber: '971508546970',
    pricing: {
      starter: {
        monthly: 185,
        yearly: 1760
      },
      business: {
        monthly: 440,
        yearly: 3960
      },
      pro: {
        monthly: 920,
        yearly: 7700
      },
      custom: {
        monthly: 'تواصل معنا',
        yearly: 'Contact Us'
      }
    }
  },
  sa: {
    currency: 'SAR',
    currencySymbol: 'ر.س',
    phoneNumber: '+971 50 854 6970',
    whatsappNumber: '971508546970',
    pricing: {
      starter: {
        monthly: 190,
        yearly: 1800
      },
      business: {
        monthly: 450,
        yearly: 4050
      },
      pro: {
        monthly: 940,
        yearly: 7875
      },
      custom: {
        monthly: 'تواصل معنا',
        yearly: 'Contact Us'
      }
    }
  }
};
// Detect country from phone number
export function detectCountryFromPhone(phone: string): Country {
  const cleanPhone = phone.replace(/[\s\-\+]/g, '');
  if (cleanPhone.startsWith('20') || cleanPhone.startsWith('01')) {
    return 'eg';
  } else if (cleanPhone.startsWith('971') || cleanPhone.startsWith('05')) {
    return 'ae';
  } else if (cleanPhone.startsWith('966') || cleanPhone.startsWith('05')) {
    return 'sa';
  }
  return 'eg'; // Default to Egypt
}
export function CountryProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [country, setCountry] = useState<Country>('eg');
  useEffect(() => {
    // First check if user is logged in and has a phone number
    const userStr = localStorage.getItem('operiens_user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.phone) {
          const detectedCountry = detectCountryFromPhone(user.phone);
          setCountry(detectedCountry);
          localStorage.setItem('operines_country', detectedCountry);
          return;
        }
        // Check if user has saved country
        if (user.country) {
          setCountry(user.country);
          localStorage.setItem('operines_country', user.country);
          return;
        }
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
    // Fall back to saved country preference
    const savedCountry = localStorage.getItem('operines_country') as Country;
    if (savedCountry && (savedCountry === 'eg' || savedCountry === 'ae' || savedCountry === 'sa')) {
      setCountry(savedCountry);
    }
  }, []);
  const handleSetCountry = (newCountry: Country) => {
    setCountry(newCountry);
    localStorage.setItem('operines_country', newCountry);
  };
  const config = COUNTRY_CONFIG[country];
  return <CountryContext.Provider value={{
    country,
    setCountry: handleSetCountry,
    currency: config.currency,
    currencySymbol: config.currencySymbol,
    phoneNumber: config.phoneNumber,
    whatsappNumber: config.whatsappNumber,
    pricing: config.pricing
  }} data-id="element-608">
      {children}
    </CountryContext.Provider>;
}
export function useCountry() {
  const context = useContext(CountryContext);
  if (context === undefined) {
    throw new Error('useCountry must be used within a CountryProvider');
  }
  return context;
}